import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { ArrowDownward, ArrowUpward, Delete, Add } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import request from "../../../api/request";
import { BackendAPI } from "../../../const/api";
import { AuthContext } from "../../../auth/authContext";
import { useToast } from "../../../component/ToastBar";

type FormatField = {
  source: string;
  header: string;
  width?: number | null;
  is_image?: boolean;
};

type ExportFormat = {
  id: string;
  name: string;
  fields: any[];
};

const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "material.code", label: "物料编码" },
  { value: "material.name", label: "物料名称" },
  { value: "material.model", label: "型号" },
  { value: "product.intro", label: "产品介绍" },
  { value: "image.main.path", label: "主图路径(文本)" },
  { value: "image.main.thumb_path", label: "主图缩略图路径(文本)" },
  { value: "image.main.id", label: "主图ID(文本)" },
];

function normalizeFields(fields: any[]): FormatField[] {
  if (!Array.isArray(fields)) return [];
  const out: FormatField[] = [];
  for (const item of fields) {
    if (!item || typeof item !== "object") continue;
    if ("source" in item && "header" in item) {
      out.push({
        source: String(item.source || ""),
        header: String(item.header || ""),
        width: item.width === undefined ? null : item.width,
        is_image: !!item.is_image,
      });
      continue;
    }
    const keys = Object.keys(item);
    if (keys.length === 1) {
      const k = keys[0];
      out.push({
        source: k,
        header: String(item[k] || ""),
        width: null,
        is_image: false,
      });
    }
  }
  return out;
}

const ProductAlbumExportFormatManager = () => {
  const toast = useToast();
  const authContext = useContext(AuthContext);

  const canManage =
    authContext?.isAdmin() ||
    authContext?.isBA() ||
    authContext?.isSalesScopeRole() ||
    authContext?.isFinance() ||
    authContext?.isWarehouse();
  const [formats, setFormats] = useState<ExportFormat[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  const [name, setName] = useState("");
  const [fields, setFields] = useState<FormatField[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const selected = useMemo(
    () => formats.find((f) => f.id === selectedId) || null,
    [formats, selectedId],
  );

  const refreshFormats = useCallback(async () => {
    const res = await request.get(BackendAPI.productAlbumExportFormats);
    if (!res?.success) return;
    const list = (res.data.formats || []) as ExportFormat[];
    setFormats(list);
    if (!selectedId && list.length) {
      setSelectedId(list[0].id);
    }
  }, [selectedId]);

  const loadSelected = useCallback(async () => {
    if (!selectedId) return;
    const res = await request.get(BackendAPI.productAlbumExportFormat(selectedId));
    if (!res?.success) return;
    const fmt = res.data.format as ExportFormat;
    setName(fmt.name || "");
    setFields(normalizeFields(fmt.fields || []));
  }, [selectedId]);

  useEffect(() => {
    if (!canManage) return;
    refreshFormats().then();
  }, [canManage, refreshFormats]);

  useEffect(() => {
    if (!canManage) return;
    loadSelected().then();
  }, [canManage, loadSelected]);

  const newFormat = () => {
    setSelectedId("");
    setName("");
    setFields([
      { source: "material.code", header: "产品编码", width: 18, is_image: false },
      { source: "material.name", header: "产品名称", width: 24, is_image: false },
      { source: "material.model", header: "型号", width: 18, is_image: false },
      { source: "product.intro", header: "产品介绍", width: 40, is_image: false },
    ]);
  };

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { source: "material.code", header: "新字段", width: null, is_image: false },
    ]);
  };

  const removeField = (idx: number) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx: number, dir: -1 | 1) => {
    setFields((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[j];
      next[j] = tmp;
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) {
      toast("格式名不能为空", "error");
      return;
    }
    if (!fields.length) {
      toast("请至少配置一列", "error");
      return;
    }
    for (const f of fields) {
      if (!f.source || !f.header) {
        toast("每一列都需要设置字段映射和表头名", "error");
        return;
      }
    }
    const payload = {
      name: name.trim(),
      fields: fields.map((f) => ({
        source: f.source,
        header: f.header,
        width: f.width === null || f.width === undefined || f.width === ("" as any) ? null : Number(f.width),
        is_image: !!f.is_image,
      })),
    };

    const res = selectedId
      ? await request.put(BackendAPI.productAlbumExportFormat(selectedId), payload)
      : await request.post(BackendAPI.productAlbumExportFormats, payload);
    if (res?.success) {
      toast("保存成功");
      await refreshFormats();
      // 如果是新建，尽量选中刚创建的（按 name 找）
      if (!selectedId) {
        const listRes = await request.get(BackendAPI.productAlbumExportFormats);
        const list = listRes?.success ? (listRes.data.formats || []) : [];
        const found = list.find((x) => x.name === payload.name);
        if (found) setSelectedId(found.id);
      }
    }
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    const res = await request.del(BackendAPI.productAlbumExportFormat(selectedId));
    if (res?.success) {
      toast("删除成功");
      setDeleteOpen(false);
      setSelectedId("");
      setName("");
      setFields([]);
      await refreshFormats();
    }
  };

  if (!canManage) {
    return <Paper style={{ padding: 16 }}>无权限</Paper>;
  }

  return (
    <Paper style={{ padding: 16 }}>
      <Box style={{ display: "flex", gap: 16 }}>
        <Box style={{ width: 280 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <Button variant="contained" onClick={newFormat} fullWidth>
              新建格式
            </Button>
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>格式列表</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formats.map((f) => (
                <TableRow
                  key={f.id}
                  hover
                  selected={f.id === selectedId}
                  onClick={() => setSelectedId(f.id)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>{f.name}</TableCell>
                </TableRow>
              ))}
              {formats.length === 0 && (
                <TableRow>
                  <TableCell style={{ color: "#666" }}>暂无格式</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <TextField
              label="格式名"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button variant="contained" onClick={save}>
              保存
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedId}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={addField}>
              添加列
            </Button>
            <span style={{ color: "#666", fontSize: 12, alignSelf: "center" }}>
              提示：图片列请把 source 选为主图相关，并勾选“图片列”
            </span>
          </div>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 72 }}>顺序</TableCell>
                <TableCell style={{ width: 220 }}>字段映射</TableCell>
                <TableCell>表头名</TableCell>
                <TableCell style={{ width: 110 }}>列宽</TableCell>
                <TableCell style={{ width: 110 }}>图片列</TableCell>
                <TableCell align="right" style={{ width: 140 }}>
                  操作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((f, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Select
                        size="small"
                        value={SOURCE_OPTIONS.some((o) => o.value === f.source) ? f.source : "__custom__"}
                        onChange={(e) => {
                          const v = String(e.target.value);
                          if (v === "__custom__") return;
                          setFields((prev) => prev.map((x, i) => (i === idx ? { ...x, source: v } : x)));
                        }}
                        style={{ width: 170 }}
                      >
                        {SOURCE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                        <MenuItem value="__custom__">自定义…</MenuItem>
                      </Select>
                      <TextField
                        size="small"
                        value={f.source}
                        onChange={(e) =>
                          setFields((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, source: e.target.value } : x)),
                          )
                        }
                        placeholder='例如：material.name + material.model'
                        fullWidth
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={f.header}
                      onChange={(e) =>
                        setFields((prev) => prev.map((x, i) => (i === idx ? { ...x, header: e.target.value } : x)))
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={f.width ?? ""}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((x, i) =>
                            i === idx ? { ...x, width: e.target.value === "" ? null : Number(e.target.value) } : x,
                          ),
                        )
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!f.is_image}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, is_image: e.target.checked } : x)),
                            )
                          }
                        />
                      }
                      label=""
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => moveField(idx, -1)} disabled={idx === 0}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveField(idx, 1)}
                      disabled={idx === fields.length - 1}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => removeField(idx)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {fields.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} style={{ color: "#666" }}>
                    请先添加列
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          确认删除格式「{selected?.name}」？删除后不可恢复（软删）。
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>取消</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProductAlbumExportFormatManager;


